/**
 * @fileoverview Machine-readable event data-quality vocabulary (COR-16).
 *
 * Today, quality "reasons" are emitted as free-text strings, which are
 * impossible to aggregate, alert on, or render consistently. This module
 * defines the canonical, machine-readable reason-code enum, its severity
 * classification, and the `QualityStatus` string-union mirror of the Prisma
 * enum.
 *
 * The severity map is a `Record<QualityReasonCode, QualitySeverity>`, so
 * adding a new reason code without classifying it is a COMPILE error. This is
 * the explainability guarantee: every code a scorer can emit has a defined
 * severity, and the type system enforces it.
 */

/**
 * Canonical machine-readable reason codes a quality scorer may emit. Grouped
 * by dimension prefix (IDENTITY_, TEMPORAL_, LOCATION_, SOURCE_, COMMERCE_,
 * CLASSIFICATION_, DUPLICATE_, REVIEW_, POLICY_, FRESHNESS_).
 */
export type QualityReasonCode =
  // Identity
  | 'IDENTITY_TITLE_MISSING'
  | 'IDENTITY_TITLE_PLACEHOLDER'
  | 'IDENTITY_DESCRIPTION_SHORT'
  | 'IDENTITY_IMAGE_MISSING'
  // IDENTITY_TITLE_NON_EVENT (PIP-407) — the title names a standing commerce
  // product, not a dated event: it ENDS in a product noun such as
  // "… | monthly membership", "DIY workshop reservation", "… gift card" (see
  // `standingProductTitleNoun` in `@loci/validation`). Anchored at the end of
  // a title segment so a show merely named after the word ("Reservation Dogs
  // Live", "Annual Membership Meeting") never matches. CAP → LOW_CONFIDENCE.
  | 'IDENTITY_TITLE_NON_EVENT'
  // Temporal
  | 'TEMPORAL_START_MISSING'
  | 'TEMPORAL_END_BEFORE_START'
  | 'TEMPORAL_DURATION_IMPLAUSIBLE'
  | 'TEMPORAL_TIMEZONE_MISSING'
  | 'TEMPORAL_START_TIME_UNKNOWN'
  // Location.
  //
  // Terminology (the `LOCATION_*` prefix is a known source of confusion):
  // these codes live on the `Location` model, which is LOCI's *venue* (the
  // model name predates the rename to "venue" — externally it is "venue"
  // everywhere). The two NAME codes below check the venue's *business /
  // display name* field (`Location.name`), which is DISTINCT from the street
  // address (`Location.address`):
  //   - LOCATION_NAME_MISSING   — `Location.name` is empty.
  //   - LOCATION_NAME_IS_ADDRESS — `Location.name` holds a street address
  //                                instead of a real venue name (COR-25,
  //                                `isAddressLikeVenueName`). Remediation:
  //                                rename the venue (admin venue form).
  // The remaining codes are about the place/address itself, so `LOCATION_*`
  // reads naturally for them.
  | 'LOCATION_NAME_MISSING'
  | 'LOCATION_NAME_IS_ADDRESS'
  | 'LOCATION_COORDS_INVALID'
  | 'LOCATION_GEOCODE_MISSING'
  | 'LOCATION_ADDRESS_CITY_ONLY'
  | 'LOCATION_VENUE_CITY_MISMATCH'
  // Source
  | 'SOURCE_ID_MISSING'
  | 'SOURCE_URL_MISSING'
  | 'SOURCE_LOW_TRUST'
  | 'SOURCE_CATEGORY_LOW_CONFIDENCE'
  // SOURCE_LINK_DEAD (COR-115) — the row's persisted link-liveness verdict
  // (`Event.linkStatus`, PIP-163 / PIP-255) is `dead`: the page the CTA would
  // open answered 404/410 or a provider "event unavailable" page. HARD_FAIL →
  // QUARANTINED, so every re-score keeps the dead-link quarantine those lanes
  // wrote instead of rewriting `qualityStatus` from structural fields alone.
  // Clears on its own once a re-check records the link live again.
  | 'SOURCE_LINK_DEAD'
  // Commerce.
  //
  // PRICE_STORY_MISSING (PIP-167, decision D6) — the event carries NO price
  // evidence of any kind: no purchasable ticket URL, no published price range,
  // no explicit "free" declaration, and no RSVP signal. Sources are sellers and
  // promoters, so a listing that says nothing about how to get in is suspect;
  // surfacing it means rendering a CTA that cannot tell the user the truth.
  // HARD_FAIL → QUARANTINED (admin-reviewable, never silently hidden).
  //
  // Unlike every other code here, this one only fires when the caller opts in
  // via `ScorePlacementContext.priceStoryRuleEnabled` (resolved from
  // `LOCI_PRICE_STORY_RULE`, default OFF) — the rule is only honest once
  // PIP-161's extraction pass has populated the price columns.
  | 'PRICE_STORY_MISSING'
  | 'COMMERCE_TICKET_URL_INVALID'
  // Classification
  | 'CLASSIFICATION_CATEGORY_MISSING'
  // Readiness
  | 'READINESS_ARTIST_BIO_MISSING'
  // Series.
  //
  // SERIES_EVERGREEN (PIP-407) — the row is one occurrence of an evergreen
  // "daily-repost" series from an open, self-serve lane: the same source, same
  // normalized title and same venue on many distinct days (the data-pipeline
  // sweep's verdict, persisted as `Event.seriesOccurrences`), where the source
  // is one of `SERIES_EVERGREEN_SOURCES` (Eventbrite, venue-site). A standing
  // product listed as daily events is not a confident placement whatever its
  // structural score, so it CAPs at LOW_CONFIDENCE. A Ticketmaster series is a
  // run of real performances and is never capped. Public list reads collapse
  // EVERY series to its next occurrence, independently of this code (and of
  // the quality gate).
  | 'SERIES_EVERGREEN'
  // Duplicate / review / policy / freshness
  | 'DUPLICATE_FUZZY_PENDING_MERGE'
  | 'REVIEW_FLAG_UNRESOLVED'
  | 'POLICY_SAFETY_FLAG'
  | 'FRESHNESS_STALE';

/**
 * How a reason code affects scoring/placement:
 * - `HARD_FAIL` — disqualifies the event outright (quarantine candidate).
 * - `PENALTY`   — subtracts from the quality score.
 * - `CAP`       — clamps the event's maximum attainable quality status.
 */
export type QualitySeverity = 'HARD_FAIL' | 'PENALTY' | 'CAP';

/**
 * Severity classification for every reason code. Typed as a
 * `Record<QualityReasonCode, QualitySeverity>` so a missing key is a COMPILE
 * error — the explainability guarantee for the scorer.
 *
 * NOTE on dual-severity codes: the PRD lists `LOCATION_COORDS_INVALID` and
 * `TEMPORAL_DURATION_IMPLAUSIBLE` as HARD_FAIL|PENALTY. They only hard-fail
 * under specific compound conditions handled directly by the scorer (e.g.
 * invalid coords combined with no geocode). Their default classification here
 * is `PENALTY`; the scorer escalates them to a hard fail when the compound
 * condition holds.
 */
export const QUALITY_REASON_SEVERITY: Record<QualityReasonCode, QualitySeverity> = {
  // HARD_FAIL — disqualifying.
  IDENTITY_TITLE_MISSING: 'HARD_FAIL',
  IDENTITY_TITLE_PLACEHOLDER: 'HARD_FAIL',
  TEMPORAL_START_MISSING: 'HARD_FAIL',
  TEMPORAL_END_BEFORE_START: 'HARD_FAIL',
  LOCATION_NAME_MISSING: 'HARD_FAIL',
  LOCATION_NAME_IS_ADDRESS: 'HARD_FAIL',
  POLICY_SAFETY_FLAG: 'HARD_FAIL',
  // PIP-167 (D6) — zero price evidence. Opt-in via the scorer context; see the
  // code's doc comment in the union above.
  PRICE_STORY_MISSING: 'HARD_FAIL',
  // COR-115 — the persisted link verdict is `dead`; see the union above.
  SOURCE_LINK_DEAD: 'HARD_FAIL',

  // CAP — clamps the maximum attainable status.
  LOCATION_ADDRESS_CITY_ONLY: 'CAP',
  SOURCE_LOW_TRUST: 'CAP',
  DUPLICATE_FUZZY_PENDING_MERGE: 'CAP',
  REVIEW_FLAG_UNRESOLVED: 'CAP',
  // PIP-407 — both cap at LOW_CONFIDENCE; see the union above.
  SERIES_EVERGREEN: 'CAP',
  IDENTITY_TITLE_NON_EVENT: 'CAP',

  // PENALTY — score deductions (includes the two dual-severity codes; see NOTE).
  IDENTITY_DESCRIPTION_SHORT: 'PENALTY',
  IDENTITY_IMAGE_MISSING: 'PENALTY',
  TEMPORAL_DURATION_IMPLAUSIBLE: 'PENALTY',
  TEMPORAL_TIMEZONE_MISSING: 'PENALTY',
  TEMPORAL_START_TIME_UNKNOWN: 'PENALTY',
  LOCATION_COORDS_INVALID: 'PENALTY',
  LOCATION_GEOCODE_MISSING: 'PENALTY',
  LOCATION_VENUE_CITY_MISMATCH: 'PENALTY',
  SOURCE_ID_MISSING: 'PENALTY',
  SOURCE_URL_MISSING: 'PENALTY',
  SOURCE_CATEGORY_LOW_CONFIDENCE: 'PENALTY',
  COMMERCE_TICKET_URL_INVALID: 'PENALTY',
  CLASSIFICATION_CATEGORY_MISSING: 'PENALTY',
  // PIP-146 — linked artists exist but none carries a real, attributed bio.
  // Only emitted when the signal is decidable (the event HAS a lineup); events
  // without artists are neutral, never penalized.
  READINESS_ARTIST_BIO_MISSING: 'PENALTY',
  FRESHNESS_STALE: 'PENALTY',
};

/**
 * Runtime-iterable list of every reason code. Derived from the severity-map
 * keys so it can never drift from {@link QUALITY_REASON_SEVERITY}.
 */
export const QUALITY_REASON_CODES: readonly QualityReasonCode[] = Object.keys(
  QUALITY_REASON_SEVERITY,
) as QualityReasonCode[];

/**
 * Quality placement status — string-union mirror of the Prisma `QualityStatus`
 * enum. Drives read-suppression and placement policy:
 * - `PREMIUM`        — highest-confidence, eligible for hero/featured surfaces.
 * - `STANDARD`       — default, eligible for normal listing.
 * - `LOW_CONFIDENCE` — listed but de-prioritized / flagged.
 * - `QUARANTINED`    — suppressed from public reads.
 */
export type QualityStatus = 'PREMIUM' | 'STANDARD' | 'LOW_CONFIDENCE' | 'QUARANTINED';

/**
 * Per-dimension subscores produced by the authoritative scorer
 * (`scoreEventForPlacement`, COR-18). Each value is on a **0–100** scale —
 * the dimension's own attainment, BEFORE the dimension weight is applied.
 *
 * The five dimensions and their LOCKED weights toward the final 0–100 score:
 * - `completeness` (30) — title/description/image/category/ticket-url presence.
 * - `location`     (25) — geocode validity, coord sanity, address specificity,
 *                          venue↔city consistency.
 * - `temporal`     (20) — end>start coherence, plausible duration, timezone,
 *                          startTimeKnown. INTEGRITY only — never freshness.
 * - `source`       (15) — provenance trust tier, sourceEventId/URL presence,
 *                          category confidence.
 * - `readiness`    (10) — priceKnown, lineup/artists, data richness.
 *
 * Persisted verbatim into the event's quality `subscores` Json column.
 */
export interface QualitySubscores {
  /** Completeness dimension attainment, 0–100. */
  readonly completeness: number;
  /** Location-integrity dimension attainment, 0–100. */
  readonly location: number;
  /** Temporal-integrity dimension attainment, 0–100. */
  readonly temporal: number;
  /** Source-trust dimension attainment, 0–100. */
  readonly source: number;
  /** Readiness dimension attainment, 0–100. */
  readonly readiness: number;
}
